// Heading hygiene for submitted rich-text content.
//
// Two rules, enforced on every path that writes submission HTML:
//
//   1. The body never carries an <h1>. A published page already has exactly
//      one H1 and the publisher emits it itself — the WP post title for a
//      standalone blog post, or the page's own H1 when an article is appended
//      to the News/Blog page. Any H1 in the body would be a second one, which
//      is an SEO problem. H1s almost always arrive by paste (Word, Google
//      Docs, another site); they are demoted to <h2>.
//   2. No empty headings. A heading with no visible text renders as nothing
//      but still lands in the document outline and in SEO crawls.
//
// server/src/lib/contentHtml.ts is a twin of this file — keep the two in sync.
// Client-side is UX (what the submitter sees is what ships); server-side is
// the guarantee (the API accepts raw HTML from any client).

const H1_OPEN = /<h1(\s[^>]*)?>/gi;
const H1_CLOSE = /<\/h1\s*>/gi;
const HEADING = /<(h[1-6])(\s[^>]*)?>([\s\S]*?)<\/\1\s*>/gi;

/**
 * True when a heading's inner HTML carries visible text. Quill's heading
 * blots hold text and inline formatting only, so tag-stripping is enough —
 * an "empty" heading is typically `<h2><br></h2>` or `<h2>&nbsp;</h2>`.
 */
function headingHasText(inner: string): boolean {
  return (
    inner
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;|&#160;|&#xa0;/gi, ' ')
      .replace(/\u00a0/g, ' ')
      .trim().length > 0
  );
}

/** Demote every <h1> to <h2>, then drop headings that have no visible text. */
export function normalizeHeadings(html: string): string {
  if (!html) return html;
  const demoted = html
    .replace(H1_OPEN, (_m, attrs: string | undefined) => `<h2${attrs ?? ''}>`)
    .replace(H1_CLOSE, '</h2>');
  return demoted.replace(HEADING, (match, _tag, _attrs, inner: string) =>
    headingHasText(inner) ? match : '',
  );
}
